<template>
    <main class="grouping-list">
        <header class="grouping-list__header">
            <button type="button" class="icon-button" title="Voltar" @click="back">
                <ArrowLeftIcon />
            </button>
            <div class="grouping-list__heading">
                <h1>Agrupamento de Ordens / Nesting</h1>
                <p>Gerencie conjuntos de ordens e seus apontamentos de produção.</p>
            </div>
            <button type="button" class="primary-button" @click="createGrouping">
                <PlusIcon />
                Novo agrupamento
            </button>
        </header>

        <section class="grouping-list__filters" aria-label="Filtros">
            <label class="search-field">
                <SearchIcon />
                <input
                    v-model.trim="filters.search"
                    type="search"
                    placeholder="Pesquisar por código, descrição ou recurso"
                    @input="scheduleLoad"
                />
            </label>
            <select v-model="filters.type" aria-label="Tipo" @change="load">
                <option value="">Todos os tipos</option>
                <option value="0">Normal</option>
                <option value="1">Nesting</option>
            </select>
            <select v-model="filters.status" aria-label="Status" @change="load">
                <option value="">Todos os status</option>
                <option value="0">Aguardando</option>
                <option value="1">Em andamento</option>
                <option value="2">Finalizado</option>
                <option value="3">Cancelado</option>
            </select>
        </section>

        <section class="grouping-list__content" aria-live="polite">
            <div v-if="loading" class="state-message">
                <LoaderIcon class="spin" />
                Carregando agrupamentos
            </div>

            <div v-else-if="error" class="state-message state-message--error">
                <AlertCircleIcon />
                <span>{{ error }}</span>
                <button type="button" @click="load">Tentar novamente</button>
            </div>

            <div v-else-if="groupings.length === 0" class="state-message">
                <GitMergeIcon />
                Nenhum agrupamento encontrado
            </div>

            <article
                v-for="grouping in paginatedGroupings"
                v-else
                :key="grouping.id"
                class="grouping-row"
            >
                <button type="button" class="grouping-row__main" @click="open(grouping.id)">
                    <span class="grouping-row__icon"><GitMergeIcon /></span>
                    <span class="grouping-row__copy">
                        <span class="grouping-row__title">
                            <strong>{{ grouping.code }}</strong>
                            <span :class="['type-pill', grouping.type === 'NESTING' ? 'type-pill--nesting' : '']">
                                {{ typeLabel(grouping.type) }}
                            </span>
                        </span>
                        <small>{{ grouping.description }}</small>
                    </span>
                    <span class="grouping-row__resource">
                        <small>Recurso</small>
                        <strong>{{ grouping.resourceName }}</strong>
                    </span>
                    <span class="grouping-row__orders">
                        <small>Ordens</small>
                        <strong>{{ grouping.orders.length }}</strong>
                    </span>
                    <span :class="['status-pill', statusClass(grouping.status)]">
                        {{ statusLabel(grouping.status) }}
                    </span>
                    <ChevronRightIcon class="grouping-row__chevron" />
                </button>
                <button
                    type="button"
                    class="grouping-row__edit"
                    title="Editar agrupamento"
                    :disabled="!isEditable(grouping)"
                    @click="edit(grouping.id)"
                >
                    <Edit2Icon />
                </button>
            </article>

            <footer v-if="!loading && !error && groupings.length > 5" class="grouping-list__pagination">
                <div class="grouping-list__pagination-meta">
                    <label>
                        Itens por página
                        <select v-model.number="pageSize" @change="currentPage = 1">
                            <option :value="5">5</option>
                            <option :value="10">10</option>
                        </select>
                    </label>
                    <span>
                        Exibindo {{ firstVisibleItem }}-{{ lastVisibleItem }} de {{ groupings.length }}
                    </span>
                </div>
                <nav aria-label="Paginação dos agrupamentos">
                    <button
                        type="button"
                        title="Página anterior"
                        :disabled="currentPage === 1"
                        @click="setPage(currentPage - 1)"
                    >
                        <ChevronLeftIcon />
                    </button>
                    <button
                        v-for="page in visiblePages"
                        :key="page"
                        type="button"
                        :class="{ active: page === currentPage }"
                        :aria-current="page === currentPage ? 'page' : null"
                        :title="`Página ${page}`"
                        @click="setPage(page)"
                    >
                        {{ page }}
                    </button>
                    <button
                        type="button"
                        title="Próxima página"
                        :disabled="currentPage === totalPages"
                        @click="setPage(currentPage + 1)"
                    >
                        <ChevronRightIcon />
                    </button>
                </nav>
            </footer>
        </section>
    </main>
</template>

<script>
import {
    AlertCircleIcon,
    ArrowLeftIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    Edit2Icon,
    GitMergeIcon,
    LoaderIcon,
    PlusIcon,
    SearchIcon
} from "vue-feather-icons";
import createOrderGroupingApi from "./orderGroupingApi";
import { apiErrorMessage, statusClass, statusLabel, typeLabel } from "./groupingPresentation";

export default {
    components: {
        AlertCircleIcon,
        ArrowLeftIcon,
        ChevronLeftIcon,
        ChevronRightIcon,
        Edit2Icon,
        GitMergeIcon,
        LoaderIcon,
        PlusIcon,
        SearchIcon
    },
    data() {
        return {
            groupings: [],
            loading: false,
            error: "",
            searchTimer: null,
            currentPage: 1,
            pageSize: 5,
            filters: {
                search: "",
                type: "",
                status: ""
            }
        };
    },
    computed: {
        siteId() {
            return this.$cookies.get("userInfo")?.currentSite;
        },
        totalPages() {
            return Math.max(1, Math.ceil(this.groupings.length / this.pageSize));
        },
        paginatedGroupings() {
            const start = (this.currentPage - 1) * this.pageSize;
            return this.groupings.slice(start, start + this.pageSize);
        },
        visiblePages() {
            const maxVisible = 5;
            let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
            const end = Math.min(this.totalPages, start + maxVisible - 1);
            start = Math.max(1, end - maxVisible + 1);
            return Array.from({ length: end - start + 1 }, (_, index) => start + index);
        },
        firstVisibleItem() {
            return (this.currentPage - 1) * this.pageSize + 1;
        },
        lastVisibleItem() {
            return Math.min(this.currentPage * this.pageSize, this.groupings.length);
        }
    },
    created() {
        this.load();
    },
    beforeDestroy() {
        window.clearTimeout(this.searchTimer);
    },
    methods: {
        statusClass,
        statusLabel,
        typeLabel,
        isEditable(grouping) {
            return grouping.status === "WAITING";
        },
        scheduleLoad() {
            window.clearTimeout(this.searchTimer);
            this.searchTimer = window.setTimeout(this.load, 300);
        },
        async load() {
            this.loading = true;
            this.error = "";

            try {
                const params = {
                    siteId: this.siteId,
                    search: this.filters.search || undefined,
                    type: this.filters.type === "" ? undefined : Number(this.filters.type),
                    status: this.filters.status === "" ? undefined : Number(this.filters.status)
                };
                this.groupings = await createOrderGroupingApi(this.$http2).list(params);
                this.currentPage = 1;
            } catch (error) {
                this.error = apiErrorMessage(error);
            } finally {
                this.loading = false;
            }
        },
        back() {
            this.$router.push({
                name: "operator-orders-menu",
                params: { prefix: this.$route.params.prefix }
            });
        },
        createGrouping() {
            this.$router.push({
                name: "operator-order-grouping-new",
                params: { prefix: this.$route.params.prefix }
            });
        },
        open(id) {
            this.$router.push({
                name: "operator-order-grouping-detail",
                params: { prefix: this.$route.params.prefix, id }
            });
        },
        edit(id) {
            this.$router.push({
                name: "operator-order-grouping-edit",
                params: { prefix: this.$route.params.prefix, id }
            });
        },
        setPage(page) {
            this.currentPage = Math.min(Math.max(page, 1), this.totalPages);
        }
    }
};
</script>

<style lang="scss" scoped>
.grouping-list {
    width: 100%;
    color: #4c4541;

    &__header {
        display: flex;
        align-items: center;
        gap: 14px;
        margin-bottom: 18px;
    }

    &__heading {
        flex: 1;
        min-width: 0;

        h1 {
            margin: 0;
            font-family: "Raleway";
            font-size: 24px;
            font-weight: 600;
        }

        p {
            margin: 3px 0 0;
            color: #998f8a;
            font-size: 13px;
        }
    }

    &__filters {
        display: grid;
        grid-template-columns: minmax(260px, 1fr) 190px 190px;
        gap: 10px;
        padding: 12px;
        border-top: 1px solid #ece0db;
        border-bottom: 1px solid #ece0db;

        select {
            height: 40px;
            border: 1px solid #cfc4be;
            border-radius: 5px;
            background: #fff;
            padding: 0 10px;
            color: #4c4541;
        }
    }

    &__content {
        padding-top: 14px;
    }

    &__pagination {
        min-height: 48px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        padding: 8px 0;
        color: #7e7570;
        font-size: 13px;

        &-meta,
        &-meta label {
            display: flex;
            align-items: center;
            gap: 10px;
        }

        &-meta {
            flex-wrap: wrap;

            select {
                height: 36px;
                border: 1px solid #cfc4be;
                border-radius: 5px;
                background: #fff;
                padding: 0 28px 0 10px;
                color: #4c4541;
            }
        }

        nav {
            display: flex;
            align-items: center;
            gap: 6px;
        }

        button {
            width: 36px;
            height: 36px;
            border: 1px solid #cfc4be;
            border-radius: 5px;
            display: grid;
            place-items: center;
            background: #fff;
            color: #6a5f59;
            font-weight: 600;

            &:hover:not(:disabled):not(.active) {
                border-color: #974900;
                color: #974900;
            }

            &.active {
                border-color: #974900;
                background: #974900;
                color: #fff;
            }

            &:disabled {
                border-color: #e8e2df;
                color: #cfc4be;
            }

            svg {
                width: 17px;
                height: 17px;
            }
        }
    }
}

.icon-button,
.primary-button {
    min-height: 40px;
    border-radius: 5px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.icon-button {
    width: 40px;
    border: 1px solid #cfc4be;
    background: #fff;
    color: #974900;
}

.primary-button {
    border: 1px solid #974900;
    background: #974900;
    color: #fff;
    padding: 0 16px;
    font-weight: 600;
}

.search-field {
    height: 40px;
    border: 1px solid #cfc4be;
    border-radius: 5px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 12px;
    background: #fff;
    color: #998f8a;

    input {
        width: 100%;
        border: 0;
        outline: 0;
        color: #4c4541;
    }
}

.grouping-row {
    display: flex;
    border: 1px solid #d8d6de;
    border-radius: 6px;
    background: #fff;
    margin-bottom: 9px;
    overflow: hidden;

    &:hover {
        border-color: #c47835;
    }

    &__main {
        flex: 1;
        min-width: 0;
        display: grid;
        grid-template-columns: 40px minmax(220px, 1fr) 180px 70px auto 20px;
        align-items: center;
        gap: 14px;
        padding: 13px 14px;
        border: 0;
        background: #fff;
        color: inherit;
        text-align: left;
    }

    &__icon {
        width: 40px;
        height: 40px;
        border-radius: 5px;
        display: grid;
        place-items: center;
        background: #f3811a;
        color: #fff;
    }

    &__copy,
    &__resource,
    &__orders {
        min-width: 0;

        small,
        strong {
            display: block;
        }

        small {
            overflow: hidden;
            color: #998f8a;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    &__title {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 3px;
    }

    &__edit {
        width: 52px;
        border: 0;
        border-left: 1px solid #ece0db;
        background: #fff;
        color: #974900;

        &:hover:not(:disabled) {
            background: #ffede2;
        }

        &:disabled {
            color: #cfc4be;
        }
    }
}

.type-pill,
.status-pill {
    display: inline-flex;
    align-items: center;
    min-height: 22px;
    border-radius: 11px;
    padding: 2px 9px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
}

.type-pill {
    background: #edf5ff;
    color: #2463a5;

    &--nesting {
        background: #f4edff;
        color: #6f42a5;
    }
}

.status--waiting { background: #fff5dc; color: #835900; }
.status--progress { background: #e7f2ff; color: #1760a5; }
.status--finished { background: #e8f7ed; color: #237a3b; }
.status--cancelled { background: #f3f1f0; color: #7e7570; }

.state-message {
    min-height: 220px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: #998f8a;

    &--error {
        color: #b42318;
    }

    button {
        border: 0;
        background: transparent;
        color: #974900;
        text-decoration: underline;
    }
}

.spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 900px) {
    .grouping-list {
        &__header {
            align-items: flex-start;
            flex-wrap: wrap;
        }

        &__heading {
            width: calc(100% - 56px);
            flex: none;
        }

        &__header .primary-button {
            width: 100%;
        }

        &__filters {
            grid-template-columns: 1fr;
        }
    }

    .grouping-row {
        &__main {
            grid-template-columns: 40px minmax(0, 1fr) auto 18px;
        }

        &__resource,
        &__orders {
            display: none;
        }
    }
}

@media (max-width: 540px) {
    .grouping-list__pagination {
        justify-content: center;
        flex-wrap: wrap;

        &-meta {
            width: 100%;
            justify-content: center;
        }

        nav {
            max-width: 100%;
        }

        button {
            width: 34px;
            height: 34px;
        }
    }

    .grouping-row {
        &__main {
            grid-template-columns: 36px minmax(0, 1fr) 18px;
            gap: 10px;
            padding: 11px;
        }

        .status-pill {
            grid-column: 2;
            justify-self: start;
        }

        &__chevron {
            grid-column: 3;
            grid-row: 1 / span 2;
        }

        &__edit {
            width: 44px;
        }
    }
}
</style>
